import BaseService from './BaseService'
import { fixImageOrientation } from '@/utils/imageOrientation'

/** Must match ModifierResolutionService::EFFECTIVE_MODIFIERS_BATCH_MAX_IDS (blu-backend). */
export const EFFECTIVE_MODIFIERS_BATCH_MAX_IDS = 100

class ProductService extends BaseService {
  constructor() {
    super('/products')
    /** @type {Map<number, Promise<object>>} */
    this._getByIdInflight = new Map()
  }

  async searchProducts (query, { includeHasPosModifiers = false, context = null } = {}) {
    const params = { query }
    if (includeHasPosModifiers) {
      params.include_has_pos_modifiers = true
    }
    if (context) {
      params.context = context
    }
    return this.customRequest('get', '/search', null, { params })
  }

  async getById (id, { context = null, includeStoreVariants = false } = {}) {
    const numId = Number(id)
    if (!Number.isFinite(numId) || numId <= 0) {
      return super.getById(id)
    }

    const cacheKey = [
      context || '',
      includeStoreVariants ? 'variants' : '',
      numId
    ].filter(Boolean).join(':')
    if (this._getByIdInflight.has(cacheKey)) {
      return this._getByIdInflight.get(cacheKey)
    }

    const params = {}
    if (context) {
      params.context = context
    }
    if (includeStoreVariants) {
      params.include_store_variants = true
    }
    const promise = this.customRequest('get', `/${numId}`, null, {
      params: Object.keys(params).length ? params : undefined
    })
      .finally(() => {
        this._getByIdInflight.delete(cacheKey)
      })
    this._getByIdInflight.set(cacheKey, promise)

    return promise
  }

  async getProductByBarcode(barcode) {
    return this.customRequest('get', `/barcode/${encodeURIComponent(barcode)}`)
  }

  async lookupByUpcExternal(upc) {
    return this.customRequest('get', '/upc-lookup', null, {
      params: { upc }
    })
  }

  async getProductImages(productId) {
    return this.customRequest('get', `/${productId}/images`)
  }

  async uploadProductImage(productId, imageFile) {
    if (!(imageFile instanceof File)) {
      throw new Error('Image must be a File object')
    }

    const fixedFile = await fixImageOrientation(imageFile)
    const formData = new FormData()
    formData.append('image', fixedFile)

    return this.customRequest('post', `/${productId}/images/upload`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    })
  }

  async importProductImageFromUrl(productId, url) {
    return this.customRequest('post', `/${productId}/images/import-url`, { url })
  }

  async updateProductImage(productId, imageId, payload) {
    return this.customRequest('patch', `/${productId}/images/${imageId}`, payload)
  }

  async deleteProductImage(productId, imageId) {
    return this.customRequest('delete', `/${productId}/images/${imageId}`)
  }

  async getEffectiveModifiers (productId, { channel = 'pos' } = {}) {
    return this.customRequest('get', `/${productId}/modifiers`, null, {
      params: { channel }
    })
  }

  /**
   * @param {number[]} productIds (caller must chunk to EFFECTIVE_MODIFIERS_BATCH_MAX_IDS or less per request)
   */
  async getEffectiveModifiersBatch (productIds, { channel = 'pos' } = {}) {
    const ids = [...new Set((productIds || []).map((v) => Number(v)).filter((n) => n > 0))]
    if (!ids.length) {
      return { channel, by_product_id: {} }
    }
    return this.customRequest('post', '/effective-modifiers/batch', {
      channel,
      product_ids: ids
    })
  }

  async checkStock(productId, locationId) {
    return this.customRequest('get', `/${productId}/stock`, { location_id: locationId })
  }

  async updateStock(productId, { quantity, type, locationId, notes = null }) {
    const data = {
      quantity,
      type,
      location_id: locationId
    }
    if (notes != null) {
      data.notes = notes
    }
    return this.customRequest('post', `/${productId}/stock`, data)
  }

  async createProduct(productData) {
    return this.create(productData)
  }

  async generateReceipt(saleId) {
    return this.customRequest('get', `/sales/${saleId}/receipt`)
  }

  async archive(productId) {
    return this.customRequest('post', `/${productId}/archive`)
  }

  async unarchive(productId) {
    return this.customRequest('post', `/${productId}/unarchive`)
  }

  async reorderStoreVariants (parentProductId, orderedVariantIds) {
    return this.customRequest('put', `/${parentProductId}/store-variant-order`, {
      ordered_variant_ids: orderedVariantIds
    })
  }

  // Pricing-related methods
  async updateCost(productId, cost, notes = null, operation = 'cost_adjustment') {
    const data = { cost, notes, operation }
    return this.customRequest('post', `/${productId}/cost`, data)
  }

  async updatePrice(productId, price, notes = null, operation = 'price_adjustment') {
    const data = { price, notes, operation }
    return this.customRequest('post', `/${productId}/price`, data)
  }

  async updatePricing(productId, cost, price, notes = null, operation = 'bulk_update') {
    const data = { cost, price, notes, operation }
    return this.customRequest('post', `/${productId}/pricing`, data)
  }

  async getPricingHistory(productId, filters = {}) {
    const params = new URLSearchParams()

    if (filters.per_page) params.append('per_page', filters.per_page)
    if (filters.page) params.append('page', filters.page)
    if (filters.operation) params.append('operation', filters.operation)
    if (filters.start_date) params.append('start_date', filters.start_date)
    if (filters.end_date) params.append('end_date', filters.end_date)

    const queryString = params.toString()
    const endpoint = `/${productId}/pricing-history${queryString ? `?${queryString}` : ''}`

    return this.customRequest('get', endpoint)
  }

  async getJustTCGFeed(productId) {
    return this.customRequest('get', `/${productId}/justtcg`)
  }

  // Enhanced product creation with pricing support
  async createProductWithPricing(productData) {
    // Ensure cost is set if not provided
    if (!productData.cost && productData.price) {
      productData.cost = productData.price
    }
    return this.create(productData)
  }

  // Enhanced product update with pricing support
  async updateProductWithPricing(productId, productData) {
    return this.update(productId, productData)
  }
}

export const productService = new ProductService()
